import * as React from 'react';

import { Stack } from '@mui/material';

import { ReminderPublisherApiObject } from '@europrocurement/l2d-domain';
import EligibilityStatusChip from './EligibilityStatusChip';

interface GlobalEligibilitySummaryProps {
    reminderPublisher?: ReminderPublisherApiObject;
}

/**
 * Composant de synthèse globale d'éligibilité pour toutes les factures d'un rappel
 *
 * Affiche un résumé sous forme de chip unique dans le header de la liste des factures.
 * Se concentre sur la vision business : combien de factures nécessitent une action du fournisseur
 * vs le nombre total de factures associées au rappel.
 *
 * @param reminderPublisher - L'objet rappel contenant toutes les factures et leurs données d'éligibilité
 */
const GlobalEligibilitySummary: React.FunctionComponent<GlobalEligibilitySummaryProps> = function ({
    reminderPublisher,
}) {
    const eligibilityStats = React.useMemo(() => {
        if (!reminderPublisher?.reminderPublisherFactures) {
            return {
                totalFactures: 0,
                eligibleFactures: 0,
            };
        }

        const totalFactures = reminderPublisher.reminderPublisherFactures.length;
        const eligibleFactures = reminderPublisher.reminderPublisherFactures.filter(
            (rpf) => rpf.hasEligibleCodes,
        ).length;

        return {
            totalFactures,
            eligibleFactures,
        };
    }, [reminderPublisher?.reminderPublisherFactures]);

    if (eligibilityStats.totalFactures === 0) {
        return null;
    }

    // Choix business : on affiche toujours UN SEUL chip qui résume la situation
    // - Si il y a des factures éligibles : chip vert "X/Y factures nécessitent une action"
    // - Si aucune facture éligible : chip gris/neutre "0/Y factures nécessitent une action"
    const hasAnyEligibleFactures = eligibilityStats.eligibleFactures > 0;

    return (
        <Stack
            direction="row"
            spacing={1}
        >
            <EligibilityStatusChip
                hasEligibleCodes={hasAnyEligibleFactures}
                eligibleCount={eligibilityStats.eligibleFactures}
                totalCount={eligibilityStats.totalFactures}
                displayMode="facturesOnly"
            />
        </Stack>
    );
};

export default GlobalEligibilitySummary;
